import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { SupporterChatRoomQuery } from 'interfaceAdapter/queries/SupporterChatRoom';
import { SupporterChatRepository } from 'interfaceAdapter/repositories/SupporterChat';
import { useLoading } from 'application/contexts/useLoading';
import { ISupporterChatRoom } from 'domain/entities/ChatRoom/SupporterChatRoom';
import { IChat } from 'domain/entities/Chat/Chat';

type SupporterChatContext = {
    loading: boolean;
    unreadIds: number[];
    chatRooms: ISupporterChatRoom[];
    filteredChatRooms: ISupporterChatRoom[];
    selectedChatRoom: ISupporterChatRoom | null;
    setSelectedChatRoom: (chatRoom: ISupporterChatRoom) => void;
    keyword: string;
    setKeyword: (keyword: string) => void;
    message: string;
    setMessage: (message: string) => void;
    sendMessage: () => void;
    fetchData: () => void;
    chatMessages: IChat[];
    fetchChatMessages: (chatRoomId: number) => void;
};

const defaultContext: SupporterChatContext = {
    loading: false,
    unreadIds: [],
    chatRooms: [],
    filteredChatRooms: [],
    selectedChatRoom: null,
    setSelectedChatRoom: () => {},
    keyword: '',
    setKeyword: () => {},
    message: '',
    setMessage: () => {},
    sendMessage: () => {},
    fetchData: () => {},
    chatMessages: [],
    fetchChatMessages: () => {},
};

export const supporterChatContext = createContext<SupporterChatContext>(defaultContext);

export const useSupporterChat = (): SupporterChatContext => {
    const diContainer = useContext(diContainerContext);
    const supporterChatRoomQuery = diContainer.resolve(SupporterChatRoomQuery);
    const supporterChatRepository = diContainer.resolve(SupporterChatRepository);
    const { loading, setLoading } = useLoading();
    const [chatRooms, setChatRooms] = useState<ISupporterChatRoom[]>([]);
    const [unreadIds, setUnreadIds] = useState<number[]>([]);
    const [filteredChatRooms, setFilteredChatRooms] = useState<ISupporterChatRoom[]>([]);
    const [selectedChatRoom, setSelectedChatRoom] = useState<ISupporterChatRoom | null>(null);
    const [keyword, setKeyword] = useState('');
    const [message, setMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<IChat[]>([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await supporterChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            const unreadIds = data.filter((chatRoom) => chatRoom.unread).map((chatRoom) => chatRoom.id);
            setUnreadIds(unreadIds);
        } catch (error) {
            console.error('チャットルーム一覧の取得に失敗しました:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (keyword.trim()) {
            const filtered = chatRooms.filter((chatRoom) => {
                const companyName = chatRoom.company?.name || '';
                const buyerName = chatRoom.buyer?.name || '';
                const searchKeyword = keyword.toLowerCase();
                return (
                    companyName.toLowerCase().includes(searchKeyword) || buyerName.toLowerCase().includes(searchKeyword)
                );
            });
            setFilteredChatRooms(filtered);
        } else {
            setFilteredChatRooms(chatRooms);
        }
    }, [keyword, chatRooms]);

    // チャットメッセージ取得
    const fetchChatMessages = useCallback(async (chatRoomId: number) => {
        setLoading(true);
        try {
            const messages = await supporterChatRoomQuery.fetchMessages(chatRoomId);
            setChatMessages(messages);
        } catch (error) {
            console.error('チャットメッセージの取得に失敗しました:', error);
        } finally {
            setLoading(false);
        }
    }, [supporterChatRoomQuery, setLoading]);

    // チャットメッセージ送信
    const sendMessage = useCallback(async () => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await supporterChatRepository.send(selectedChatRoom.id, message);
            setMessage('');
            // チャットメッセージを更新
            await fetchChatMessages(selectedChatRoom.id);
            // チャットルーム一覧を更新
            const data = await supporterChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            const unreadIds = data.filter((chatRoom) => chatRoom.unread).map((chatRoom) => chatRoom.id);
            setUnreadIds(unreadIds);
        } catch (error) {
            console.error('メッセージ送信エラー:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedChatRoom, message, supporterChatRepository, supporterChatRoomQuery, fetchChatMessages, setLoading]);

    return {
        loading,
        unreadIds,
        chatRooms,
        filteredChatRooms,
        selectedChatRoom,
        setSelectedChatRoom,
        keyword,
        setKeyword,
        message,
        setMessage,
        sendMessage,
        fetchData,
        chatMessages,
        fetchChatMessages,
    };
};
